import { useCallback, useState } from 'react';
import { motion } from 'framer-motion';

const REVEAL_RISE = 16;
const loadedImageUrls = new Set();

export function preloadImages(urls) {
    urls.forEach((imageURL) => {
        if (!imageURL || loadedImageUrls.has(imageURL)) return;
        const image = new Image();
        image.onload = () => loadedImageUrls.add(imageURL);
        image.src = imageURL;
    });
}

function useImageReady(imageURL, onReady) {
    const [ready, setReady] = useState(false);
    const [trackedURL, setTrackedURL] = useState(imageURL);

    if (imageURL !== trackedURL) {
        setTrackedURL(imageURL);
        setReady(false);
    }

    const markReady = useCallback(() => {
        if (!imageURL) return;
        loadedImageUrls.add(imageURL);
        setReady(true);
        onReady?.();
    }, [imageURL, onReady]);

    const bindImage = useCallback((node) => {
        if (node && node.complete && node.naturalWidth > 0) markReady();
    }, [markReady]);

    return { ready, markReady, bindImage };
}

export default function RevealImage({
    src,
    alt = '',
    draggable = false,
    className,
    style,
    onReady,
    settle = false,
}) {
    const { ready, markReady, bindImage } = useImageReady(src, onReady);
    const skipRise = settle && loadedImageUrls.has(src);
    const hidden = { opacity: 0, y: skipRise ? 0 : REVEAL_RISE };

    return (
        <motion.div
            className={className}
            style={style}
            initial={hidden}
            animate={ready ? { opacity: 1, y: 0 } : hidden}
            transition={{ duration: skipRise ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
            <img
                ref={bindImage}
                src={src}
                alt={alt}
                draggable={draggable}
                onLoad={markReady}
            />
        </motion.div>
    );
}
